import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

export interface DiscoveryProfileCard {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  tagline: string | null;
  location: string | null;
  avatarUrl: string | null;
  roleType: string;
  tags: string[];
  themeId: string;
  accentColor: string;
  viewCount: number;
}

export interface DiscoveryCursor {
  updatedAtMillis: number;
  id: string;
}

export async function claimProfileSlug(
  profileId: string,
  slug: string,
): Promise<{ slug: string; available: boolean }> {
  const fn = httpsCallable<{ profileId: string; slug: string }, { slug: string; available: boolean }>(
    functions,
    'claimProfileSlug',
  );
  const result = await fn({ profileId, slug });
  return result.data;
}

export async function recordProfileView(payload: {
  profileId?: string;
  slug?: string;
}): Promise<void> {
  const fn = httpsCallable<typeof payload, { ok: boolean }>(functions, 'recordProfileView');
  await fn(payload);
}

export async function recordComponentClick(payload: {
  profileId?: string;
  slug?: string;
  componentId: string;
}): Promise<void> {
  const fn = httpsCallable<typeof payload, { ok: boolean }>(functions, 'recordComponentClick');
  await fn(payload);
}

export async function searchProfiles(payload: {
  query?: string;
  roleTypes?: string[];
  tags?: string[];
  pageSize?: number;
  cursor?: DiscoveryCursor | null;
  includeAllTags?: boolean;
}): Promise<{
  items: DiscoveryProfileCard[];
  total: number | null;
  nextCursor: DiscoveryCursor | null;
  allTags?: string[];
}> {
  const fn = httpsCallable<
    typeof payload,
    {
      items: DiscoveryProfileCard[];
      total: number | null;
      nextCursor: DiscoveryCursor | null;
      allTags?: string[];
    }
  >(functions, 'searchProfiles');
  const result = await fn(payload);
  return result.data;
}

export async function issueProfilePreviewToken(
  profileId: string,
): Promise<{ token: string; expiresInSeconds: number }> {
  const fn = httpsCallable<{ profileId: string }, { token: string; expiresInSeconds: number }>(
    functions,
    'issueProfilePreviewToken',
  );
  const result = await fn({ profileId });
  return result.data;
}

export async function resolveProfilePreview(token: string): Promise<{
  profile: Record<string, unknown> & { id: string };
  components: Array<Record<string, unknown> & { id: string }>;
}> {
  const fn = httpsCallable<
    { token: string },
    {
      profile: Record<string, unknown> & { id: string };
      components: Array<Record<string, unknown> & { id: string }>;
    }
  >(functions, 'resolveProfilePreview');
  const result = await fn({ token });
  return result.data;
}
